"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";

export interface BookingTimeslot {
  start_time: string;
  end_time: string;
}

export interface BookingCalendarTexts {
  eyebrow: string;
  heading: string;
  subheading: string;
  stepDate: string;
  stepTime: string;
  stepDetails: string;
  pickDateHint: string;
  pickTimeHint: string;
  reviewHeading: string;
  dateRowLabel: string;
  timeRowLabel: string;
  modifyTime: string;
  back: string;
  continue: string;
  loading: string;
  loadError: string;
  retry: string;
  noAvailability: string;
  noSlotsForDate: string;
  nameLabel: string;
  namePlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  occasionLabel: string;
  occasionPlaceholder: string;
  occasionOptions: string[];
  notesLabel: string;
  notesPlaceholder: string;
  submit: string;
  submitting: string;
  successTitle: string;
  successText: string;
  bookAnother: string;
  errorTitle: string;
  errorRequired: string;
  errorPhone: string;
  months: string[];
  weekdays: string[];
}

export interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts?: Partial<BookingCalendarTexts>;
  containerStyle?: CSSProperties;
  className?: string;
}

const DEFAULT_TEXTS: BookingCalendarTexts = {
  eyebrow: "Booking",
  heading: "Choose a time",
  subheading: "Pick a date, choose a slot and tell us who you are.",
  stepDate: "Date",
  stepTime: "Time",
  stepDetails: "Details",
  pickDateHint: "Select an available date.",
  pickTimeHint: "Select an available time.",
  reviewHeading: "Your booking",
  dateRowLabel: "Date",
  timeRowLabel: "Time",
  modifyTime: "Modify time",
  back: "Back",
  continue: "Continue",
  loading: "Loading availability...",
  loadError: "We couldn't load availability right now.",
  retry: "Try again",
  noAvailability: "No times are available at the moment. Please check back soon.",
  noSlotsForDate: "No times left for this date. Please pick another.",
  nameLabel: "Name",
  namePlaceholder: "Your full name",
  phoneLabel: "Phone",
  phonePlaceholder: "Your phone number",
  occasionLabel: "Occasion",
  occasionPlaceholder: "Select an occasion",
  occasionOptions: ["Other"],
  notesLabel: "Notes",
  notesPlaceholder: "Anything we should know?",
  submit: "Confirm booking",
  submitting: "Booking...",
  successTitle: "Your time is booked",
  successText: "We've saved your booking. You'll hear from us shortly.",
  bookAnother: "Book another time",
  errorTitle: "Something went wrong. Please try again.",
  errorRequired: "Please enter your name.",
  errorPhone: "Please enter a valid phone number.",
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

const EMPTY_FORM = {
  customer_name: "",
  customer_phone: "",
  occasion: "",
  notes: "",
};

const pad2 = (value: number) => String(value).padStart(2, "0");

const dateOf = (value: string) => (value || "").split(" ")[0];

const timeOf = (value: string) => {
  const parts = (value || "").split(" ");
  if (parts.length < 2) return "";
  const hm = parts[1].split(":");
  return `${hm[0] ?? ""}:${hm[1] ?? ""}`;
};

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const monthOf = (date: string) => ({
  y: Number(date.slice(0, 4)),
  m: Number(date.slice(5, 7)) - 1,
});

const buildCells = (year: number, month: number) => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const normalizeSlots = (data: unknown): BookingTimeslot[] => {
  let source: unknown = data;
  if (data && !Array.isArray(data) && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    source = obj.data ?? obj.timeslots ?? obj.slots ?? obj.result ?? obj.items ?? [];
  }
  if (!Array.isArray(source)) return [];
  return source
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    .map((item) => ({
      start_time: String(item.start_time ?? ""),
      end_time: String(item.end_time ?? ""),
    }))
    .filter((slot) => dateOf(slot.start_time) !== "");
};

export default function BookingCalendar({
  timeslotsApi,
  appointmentsApi,
  texts,
  containerStyle,
  className = "",
}: BookingCalendarProps) {
  const t = { ...DEFAULT_TEXTS, ...texts };
  const formRef = useRef<HTMLFormElement>(null);

  const [slots, setSlots] = useState<BookingTimeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [viewMonth, setViewMonth] = useState<{ y: number; m: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<BookingTimeslot | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [status, setStatus] = useState<"idle" | "submitting">("idle");
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const loadTimeslots = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetch(timeslotsApi);
      if (!response.ok) throw new Error("request failed");
      const data = await response.json();
      setSlots(normalizeSlots(data));
    } catch {
      setLoadError(t.loadError);
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi, t.loadError]);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (active) await loadTimeslots();
    })();
    return () => {
      active = false;
    };
  }, [loadTimeslots]);

  const availableDates = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((slot) => {
      const date = dateOf(slot.start_time);
      if (date) set.add(date);
    });
    return Array.from(set).sort();
  }, [slots]);

  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);

  const slotsByDate = useMemo(() => {
    const map = new Map<string, BookingTimeslot[]>();
    slots.forEach((slot) => {
      const date = dateOf(slot.start_time);
      if (!date) return;
      const list = map.get(date) ?? [];
      list.push(slot);
      map.set(date, list);
    });
    map.forEach((list) => list.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [slots]);

  // Keep the selected date and calendar month aligned with real availability.
  useEffect(() => {
    if (availableDates.length === 0) return;
    const today = todayString();
    const anchor = availableDates.find((date) => date >= today) ?? availableDates[availableDates.length - 1];
    const anchorMonth = monthOf(anchor);

    setSelectedDate((prev) => (prev && availableSet.has(prev) ? prev : anchor));
    setViewMonth((prev) => {
      if (!prev) return anchorMonth;
      const hasAvailability = availableDates.some((date) => {
        const m = monthOf(date);
        return m.y === prev.y && m.m === prev.m;
      });
      return hasAvailability ? prev : anchorMonth;
    });
  }, [availableDates, availableSet]);

  const cells = useMemo(
    () => (viewMonth ? buildCells(viewMonth.y, viewMonth.m) : []),
    [viewMonth]
  );

  const changeMonth = useCallback((delta: number) => {
    setViewMonth((prev) => {
      if (!prev) return prev;
      let month = prev.m + delta;
      let year = prev.y;
      while (month < 0) {
        month += 12;
        year -= 1;
      }
      while (month > 11) {
        month -= 12;
        year += 1;
      }
      return { y: year, m: month };
    });
  }, []);

  const resetSelection = () => {
    setSelectedSlot(null);
    setSelectedDate(null);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setCurrentStep(0);
  };

  const buildNotes = (occasion: string, message: string) => {
    const extensions: Record<string, string> = {};
    if (occasion) extensions.occasion = occasion;
    if (Object.keys(extensions).length > 0) {
      return JSON.stringify({ message, extensions });
    }
    return message;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;
    if (!selectedSlot) {
      setCurrentStep(1);
      return;
    }

    const data = formRef.current ? new FormData(formRef.current) : null;
    const name = String(data?.get("customer_name") ?? form.customer_name).trim();
    const phone = String(data?.get("customer_phone") ?? form.customer_phone).trim();
    const occasion = String(data?.get("occasion") ?? form.occasion).trim();
    const notes = String(data?.get("notes") ?? form.notes).trim();

    setFormError("");
    if (!name) {
      setFormError(t.errorRequired);
      return;
    }
    if (phone.replace(/\D/g, "").length < 6) {
      setFormError(t.errorPhone);
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch(appointmentsApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: name,
          customer_phone: phone,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: buildNotes(occasion, notes),
        }),
      });
      if (!response.ok) throw new Error("booking failed");

      setSuccess(true);
      setStatus("idle");
      resetSelection();
      await loadTimeslots();
    } catch {
      setStatus("idle");
      setFormError(t.errorTitle);
    }
  };

  const selectedSlots = selectedDate ? slotsByDate.get(selectedDate) ?? [] : [];
  const steps = [t.stepDate, t.stepTime, t.stepDetails];

  return (
    <div
      className={`rounded-lg border border-foreground-950/12 bg-background-50 p-6 md:p-10 ${className}`}
      style={containerStyle}
    >
      <p className="eyebrow">{t.eyebrow}</p>
      <h3 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
        {t.heading}
      </h3>
      <p className="mt-3 max-w-xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
        {t.subheading}
      </p>

      <ol className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
        {steps.map((label, index) => {
          const active = index === currentStep;
          const done = index < currentStep;
          return (
            <li key={label} className="flex items-center gap-3">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full border font-label text-[11px] transition-colors duration-300 ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : done
                      ? "border-primary-300 bg-primary-100 text-primary-700"
                      : "border-foreground-950/20 text-foreground-400"
                }`}
              >
                {done ? <i className="ri-check-line text-sm" /> : index + 1}
              </span>
              <span
                className={`font-label text-[10px] uppercase tracking-label ${
                  active ? "text-foreground-950" : "text-foreground-400"
                }`}
              >
                {label}
              </span>
              {index < steps.length - 1 && (
                <span className="hidden h-px w-8 bg-foreground-950/15 sm:block" />
              )}
            </li>
          );
        })}
      </ol>

      {success && (
        <div className="mt-8 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-100/70 p-5">
          <i className="ri-calendar-check-line mt-0.5 text-lg text-primary-700" />
          <div>
            <p className="font-heading text-lg text-foreground-950">{t.successTitle}</p>
            <p className="mt-1 font-body text-sm text-foreground-600">{t.successText}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center gap-3 font-body text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg" />
          {t.loading}
        </div>
      ) : loadError ? (
        <div className="mt-10 flex flex-col items-start gap-4 rounded-md border border-foreground-950/12 p-6">
          <p className="flex items-center gap-2 font-body text-sm text-foreground-600">
            <i className="ri-error-warning-line text-lg" />
            {loadError}
          </p>
          <button type="button" onClick={loadTimeslots} className="btn-primary">
            {t.retry}
          </button>
        </div>
      ) : availableDates.length === 0 ? (
        <p className="mt-10 font-body text-sm text-foreground-500">{t.noAvailability}</p>
      ) : (
        <div className="mt-10">
          {currentStep === 0 && (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => changeMonth(-1)}
                    aria-label="Previous month"
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-foreground-950/15 text-foreground-600 transition-colors duration-300 hover:border-primary-500 hover:text-primary-600"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <p className="font-heading text-xl text-foreground-950">
                    {t.months[viewMonth?.m ?? 0]} {viewMonth?.y ?? ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => changeMonth(1)}
                    aria-label="Next month"
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-foreground-950/15 text-foreground-600 transition-colors duration-300 hover:border-primary-500 hover:text-primary-600"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-7 gap-1">
                  {t.weekdays.map((day) => (
                    <div
                      key={day}
                      className="pb-2 text-center font-label text-[10px] uppercase tracking-label text-foreground-400"
                    >
                      {day}
                    </div>
                  ))}
                  {cells.map((day, index) => {
                    if (day === null) {
                      return <div key={`empty-${index}`} className="h-16" />;
                    }
                    const dateStr = `${viewMonth?.y}-${pad2((viewMonth?.m ?? 0) + 1)}-${pad2(day)}`;
                    const available = availableSet.has(dateStr);
                    const selected = dateStr === selectedDate;
                    const weekday = t.weekdays[new Date(viewMonth?.y ?? 0, viewMonth?.m ?? 0, day).getDay()];
                    return (
                      <button
                        key={dateStr}
                        type="button"
                        disabled={!available}
                        onClick={() => {
                          setSelectedDate(dateStr);
                          setSelectedSlot(null);
                        }}
                        className={`flex h-16 cursor-pointer flex-col items-center justify-center rounded-md border text-center transition-all duration-300 ${
                          selected
                            ? "border-primary-500 bg-primary-500 text-background-50"
                            : available
                              ? "border-primary-200 bg-primary-100/50 text-foreground-950 hover:border-primary-400"
                              : "cursor-not-allowed border-transparent text-foreground-300"
                        }`}
                      >
                        <span className="font-label text-[9px] uppercase tracking-label opacity-70">
                          {weekday}
                        </span>
                        <span className="font-heading text-lg leading-none">{day}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-md border border-foreground-950/12 bg-background-100 p-6">
                  <p className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                    {t.pickDateHint}
                  </p>
                  <p className="mt-4 font-heading text-2xl text-foreground-950">
                    {selectedDate ?? "—"}
                  </p>
                  <button
                    type="button"
                    disabled={!selectedDate}
                    onClick={() => {
                      setSuccess(false);
                      setCurrentStep(1);
                    }}
                    className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t.continue}
                    <i className="ri-arrow-right-line text-base" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className="font-heading text-2xl text-foreground-950">{selectedDate}</p>
                <p className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                  {t.pickTimeHint}
                </p>
              </div>

              {selectedSlots.length === 0 ? (
                <p className="mt-6 font-body text-sm text-foreground-500">
                  {t.noSlotsForDate}
                </p>
              ) : (
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {selectedSlots.map((slot) => (
                    <button
                      key={slot.start_time}
                      type="button"
                      onClick={() => {
                        setSelectedSlot(slot);
                        setFormError("");
                        setSuccess(false);
                        setCurrentStep(2);
                      }}
                      className="cursor-pointer rounded-md border border-foreground-950/15 px-3 py-3 font-body text-sm text-foreground-800 transition-all duration-300 hover:border-primary-500 hover:bg-primary-100/60 hover:text-foreground-950"
                    >
                      {timeOf(slot.start_time)} – {timeOf(slot.end_time)}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => setCurrentStep(0)}
                  className="inline-flex cursor-pointer items-center gap-2 font-label text-[11px] uppercase tracking-label text-foreground-600 transition-colors duration-300 hover:text-primary-600"
                >
                  <i className="ri-arrow-left-line text-base" />
                  {t.back}
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && selectedSlot && (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <div className="rounded-md border border-foreground-950/12 bg-background-100 p-6">
                  <p className="font-heading text-xl text-foreground-950">
                    {t.reviewHeading}
                  </p>
                  <div className="mt-5 space-y-4">
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                        {t.dateRowLabel}
                      </p>
                      <p className="mt-1 font-body text-sm text-foreground-950">
                        {selectedDate}
                      </p>
                    </div>
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-label text-foreground-500">
                        {t.timeRowLabel}
                      </p>
                      <p className="mt-1 font-body text-sm text-foreground-950">
                        {timeOf(selectedSlot.start_time)} – {timeOf(selectedSlot.end_time)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="mt-6 inline-flex cursor-pointer items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700 transition-colors duration-300 hover:text-primary-600"
                  >
                    <i className="ri-edit-line text-base" />
                    {t.modifyTime}
                  </button>
                </div>
              </div>

              <div className="lg:col-span-8">
                <form ref={formRef} onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2">
                    <div>
                      <label htmlFor="bk-name" className="field-label">
                        {t.nameLabel}
                      </label>
                      <input
                        id="bk-name"
                        name="customer_name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder={t.namePlaceholder}
                        value={form.customer_name}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, customer_name: event.target.value }))
                        }
                        className="field-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="bk-phone" className="field-label">
                        {t.phoneLabel}
                      </label>
                      <input
                        id="bk-phone"
                        name="customer_phone"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder={t.phonePlaceholder}
                        value={form.customer_phone}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, customer_phone: event.target.value }))
                        }
                        className="field-input"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="bk-occasion" className="field-label">
                        {t.occasionLabel}
                      </label>
                      <select
                        id="bk-occasion"
                        name="occasion"
                        value={form.occasion}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, occasion: event.target.value }))
                        }
                        className="field-select"
                      >
                        <option value="">{t.occasionPlaceholder}</option>
                        {t.occasionOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-8">
                    <label htmlFor="bk-notes" className="field-label">
                      {t.notesLabel}
                    </label>
                    <textarea
                      id="bk-notes"
                      name="notes"
                      rows={4}
                      maxLength={500}
                      placeholder={t.notesPlaceholder}
                      value={form.notes}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, notes: event.target.value }))
                      }
                      className="field-textarea"
                    />
                  </div>

                  {formError && (
                    <p className="mt-5 flex items-center gap-2 font-body text-sm text-red-700">
                      <i className="ri-error-warning-line text-base" />
                      {formError}
                    </p>
                  )}

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? t.submitting : t.submit}
                      {status !== "submitting" && (
                        <i className="ri-calendar-check-line text-base" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="inline-flex cursor-pointer items-center gap-2 font-label text-[11px] uppercase tracking-label text-foreground-600 transition-colors duration-300 hover:text-primary-600"
                    >
                      <i className="ri-arrow-left-line text-base" />
                      {t.back}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}